import { api } from 'api';
import {
  CALCULATOR_FORM_PREFIX,
  CONFIG_OCCUPATION_EXTRAS,
  COUNTRIES,
  DESKTOP_MIN_WIDTH,
  EN_LANG,
  FX_CALCULATOR_FORM_PREFIX,
  GREETING_AFTERNOON_TEXT,
  GREETING_EVENING_TEXT,
  GREETING_MORNING_TEXT,
  I18N_REDIRECTED,
  TABLET_MIN_WIDTH,
} from 'constants';
import Cookies from 'helpers/cookies';
import { checkMaintenance } from 'helpers/maintenance';
import { isEmpty } from 'lodash';
import { domUtils } from 'utils';

export const getItemsConfig = (
  items = [],
  countryCode = null,
  dmCode = null,
  spCode = null,
  currencyCode = null,
  item = {
    id: '',
    prop: '',
  },
) => {
  let itemConfig;
  const isCheckItemSpecific = !isEmpty(item) && item?.id && item?.prop;

  const configByGlobal = items?.find((i) => !i?.countryCode) || items[0];

  const configByCountryCode = countryCode
    ? items?.find((i) => i?.countryCode === countryCode && !i?.[item?.prop]) ||
      configByGlobal
    : configByGlobal;

  if (countryCode && dmCode && spCode && currencyCode)
    return (itemConfig =
      items?.find(
        (i) =>
          i?.countryCode === countryCode &&
          i?.dmCode === dmCode &&
          i?.spCode === spCode &&
          i?.currencyCode === currencyCode,
      ) || configByCountryCode);

  if (countryCode && dmCode && spCode)
    return (itemConfig =
      items?.find(
        (i) =>
          i?.countryCode === countryCode &&
          i?.dmCode === dmCode &&
          i?.spCode === spCode,
      ) || configByCountryCode);

  if (countryCode && dmCode && currencyCode)
    return (itemConfig =
      items?.find(
        (i) =>
          i?.countryCode === countryCode &&
          i?.dmCode === dmCode &&
          i?.currencyCode === currencyCode,
      ) || configByCountryCode);

  if (countryCode && spCode && currencyCode)
    return (itemConfig =
      items?.find(
        (i) =>
          i?.countryCode === countryCode &&
          i?.spCode === spCode &&
          i?.currencyCode === currencyCode,
      ) || configByCountryCode);

  if (countryCode && dmCode)
    return (itemConfig =
      items?.find(
        (i) => i?.countryCode === countryCode && i?.dmCode === dmCode,
      ) || configByCountryCode);

  if (countryCode && spCode)
    return (itemConfig =
      items?.find(
        (i) => i?.countryCode === countryCode && i?.spCode === spCode,
      ) || configByCountryCode);

  if (countryCode && isCheckItemSpecific)
    return (itemConfig =
      items?.find(
        (i) =>
          (i?.countryCode === countryCode && i?.[item?.prop] === item?.id) ||
          (!i?.countryCode && i?.[item?.prop] === item?.id),
      ) || configByCountryCode);

  if (countryCode && !dmCode && !spCode && !currencyCode)
    itemConfig = configByCountryCode;

  return itemConfig || configByCountryCode;
};

export const getOccupationOptions = (occupations) => {
  if (isEmpty(occupations)) return [];
  return [
    ...occupations?.filter((o) => CONFIG_OCCUPATION_EXTRAS?.includes(o?.key)),
    ...(occupations?.filter(
      (o) => o?.key !== 'UNEMPLOYED' && o?.key !== 'RETIRED',
    ) || []),
  ];
};

export const getPrevOccupationOptions = (job, occupations) => {
  if (isEmpty(occupations)) return [];
  if (job === 'RETIRED') return occupations.filter((o) => o?.key !== 'RETIRED');
  if (job === 'UNEMPLOYED')
    return occupations.filter(
      (o) => o?.key !== 'UNEMPLOYED' && o?.key !== 'RETIRED',
    );
  return occupations;
};

export const fetchCountries = async (callback) => {
  try {
    const { data } = await api.getCountries();

    if (data) {
      callback(data);
    }
  } catch (error) {
    checkMaintenance(error);
    console.error(error?.message);
  }
};

export const getGreetingText = () => {
  const currentHours = new Date().getHours();

  if (currentHours >= 5 && currentHours < 12) return GREETING_MORNING_TEXT;

  if (currentHours >= 12 && currentHours < 17) return GREETING_AFTERNOON_TEXT;

  return GREETING_EVENING_TEXT;
};

export const ENTER_CODE = 13;
export const BACKSPACE_CODE = 8;
export const CTRL_CODE = 17;
export const CTRLA_CODE = 65;
export const DELETE_CODE = 46;
export const NEXT_ARROW_CODE = 39;
export const PREV_ARROW_CODE = 37;

export const preventKeyDownEnter = (e) => {
  if (e.which === ENTER_CODE) {
    e.preventDefault();
    e.stopPropagation();
  }
};

export const preventOnClick = (e) => {
  if (!e) return;
  e.preventDefault();
  e.stopPropagation();
};

export const checkPaymentNumberLengthValid = (value, length) => {
  if (length?.min === length?.max && value.length === length?.max) return true;

  if (value.length >= length?.min && value.length <= length?.max) return true;

  return false;
};

const handleFieldValidation = (
  value,
  name,
  validation,
  msgInvalid,
  checkValidFn,
) => {
  if (value) {
    const isValid = checkValidFn ? Boolean(checkValidFn(value)) : true;

    if (isValid) {
      validation = {
        ...validation,
        [name]: '',
      };
    }

    if (!isValid) {
      validation = {
        ...validation,
        [name]: msgInvalid,
      };
    }
  }

  return validation;
};
const handleFieldEmptyValidation = (value, name, validation, msgRequired) => {
  if (!value) {
    validation = {
      ...validation,
      [name]: msgRequired,
    };
  }

  return validation;
};
export const checkFormInputValidation = (value, field, validation) => {
  const { name, checkValidFn, msgInvalid, msgRequired } = field;

  validation = handleFieldEmptyValidation(value, name, validation, msgRequired);

  validation = handleFieldValidation(
    value,
    name,
    validation,
    msgInvalid,
    checkValidFn,
  );

  return validation;
};

export const checkFormInputPortalCodeValidation = (
  value,
  field,
  validation,
  config,
) => {
  const { name, checkValidFn, msgInvalid, msgRequired } = field;
  const numberMaxLength = config?.max;
  const numberMinLength = config?.min;
  const isNumberMaxEqualNumberMin =
    numberMaxLength && numberMinLength && numberMaxLength === numberMinLength;

  validation = handleFieldEmptyValidation(value, name, validation, msgRequired);

  validation = handleFieldValidation(
    value,
    name,
    validation,
    msgInvalid,
    checkValidFn,
  );

  if (value) {
    if (value.length < numberMinLength) {
      validation = {
        ...validation,
        [name]: msgInvalid,
      };
    }

    if (isNumberMaxEqualNumberMin && value.length < numberMinLength) {
      validation = {
        ...validation,
        [name]: msgInvalid,
      };
    }
  }

  return validation;
};

export const findCountry = (value) => COUNTRIES.find((c) => c?.value === value);
export const findCountryWithCountryName = (countryName, countryCode) =>
  COUNTRIES.find((c) => c?.label === countryName || c?.value === countryCode);

export const fetchCountryConfig = async (countryCode) => {
  try {
    const { data } = await api.getCountryConfig(countryCode);

    return data;
  } catch (error) {
    checkMaintenance(error);
    console.error(error?.message);

    return null;
  }
};

export const getHeaderEaPlatform = () =>
  window.innerWidth >= DESKTOP_MIN_WIDTH
    ? 'web-desktop'
    : window.innerWidth < TABLET_MIN_WIDTH
      ? 'web-mobile'
      : 'web-tablet';

export const COLLAPSED_WIDTH = 108;
export const COLLAPSE_WIDTH = 300;
export const getCollapsibleSidebarWidth = (isMenuExpand) => {
  if (isMenuExpand) return `${COLLAPSE_WIDTH}px`;
  return `${COLLAPSED_WIDTH}px`;
};

export const handleMobileDeviceShare = async (url) => {
  if ('share' in navigator) {
    navigator
      .share({
        // title: '',
        // text: '',
        url,
      })
      .then(() => {
        console.log('Thanks for sharing!');
      })
      .catch((error) => {
        console.error('Error sharing:', error);
      });
  } else {
    console.log('Web Share API not supported.');
  }
};

export const getImgSrcByLang = (ImgEn, ImgVi) => {
  const locate =
    localStorage?.getItem(I18N_REDIRECTED) ||
    Cookies.get(I18N_REDIRECTED) ||
    domUtils.getQueryWithName('lang') ||
    EN_LANG;
  const isEn = locate === EN_LANG;
  if (isEn) return ImgEn;
  return ImgVi;
};

export const checkDomainAllowFetchDataConfig = () => {
  const paths = [CALCULATOR_FORM_PREFIX, FX_CALCULATOR_FORM_PREFIX];
  const appHosts = [
    // development
    'locahost',
    // preview
    '.d2t600k0tsg1iv.amplifyapp.com',
    '.d1lu1wfxip3eo0.amplifyapp.com',
    'app-pr.hhmt.au',
    // staging
    'staging-app.hhmt.au',
    'staging-app.remox.au',
    // production
    'app.hhmt.com.au',
    'app.remox.com.au',
  ];
  const webflowHosts = [
    // staging
    'staging-hhmt.webflow.io',
    'staging-remox.webflow.io',
    // production
    'hhmt.com.au',
    'remox.com.au',
  ];
  if (
    paths.find(
      (path) =>
        window?.location?.pathname?.toLowerCase() === path?.toLowerCase(),
    )
  )
    return false;
  if (
    webflowHosts.find(
      (host) =>
        window?.parent?.location?.host?.toLowerCase() === host?.toLowerCase(),
    )
  )
    return false;
  if (
    appHosts.find(
      (host) => window?.location?.host?.toLowerCase() === host?.toLowerCase(),
    )
  )
    return true;
  return true;
};

export const timeoutFn = (fn = () => {}, delay = 300) => {
  const timer = setTimeout(() => {
    fn();
    clearTimeout(timer);
  }, delay);
};

export const getFirstNameLastName = ({ lastName, firstName }) => {
  if (lastName && firstName) return `${firstName} ${lastName}`?.toUpperCase();

  if (lastName && !firstName) return `${lastName}`?.toUpperCase();

  if (!lastName && firstName) return `${firstName}`?.toUpperCase();

  return '';
};
